import React, { useRef, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* =======================================================================
 * Wizard de Fotos Guiadas — Modelo de SÉRIES REPETÍVEIS
 *
 * Cada etapa tem um conjunto de "tomadas" (shots) padronizadas. O usuário
 * cria uma ou mais SÉRIES (ex.: "Mármore Calacatta 1", "Frete 2", "Banheiro
 * Master") e tira as N fotos previstas em cada série.
 *
 * Config map: ETAPA_NOME -> {
 *    serieLabel:     rótulo do que é uma série ("Material", "Frete", "Peça"…)
 *    seriePlaceholder: placeholder do input de nome
 *    shots: [{ id, nome, descricao }, …]
 *    extras: lista de campos opcionais por série (ex: origem Brasil/Exterior)
 * }
 * =======================================================================*/

export const WIZARD_ETAPA_CONFIG = {
  "Compra dos Materiais": {
    serieLabel: "Material",
    seriePlaceholder: "Ex: Mármore Calacatta 1, Quartzo Branco 2",
    shots: [
      { id: "vista_geral", nome: "VISTA GERAL DA CHAPA", descricao: "Fotografe a chapa inteira de frente, mostrando toda a extensão" },
      { id: "detalhe_textura", nome: "DETALHE DA TEXTURA / VEIOS", descricao: "Aproxime a câmera para mostrar a textura e veios do material" },
      { id: "etiqueta_nf", nome: "ETIQUETA / NOTA FISCAL", descricao: "Fotografe a etiqueta, código de lote ou nota fiscal do material" },
    ],
    extras: [
      { key: "serie_origem", label: "Origem", type: "select", options: ["Brasil", "Exterior"], default: "Brasil" },
    ],
  },
  "Frete para Beneficiamento de Superfície": {
    serieLabel: "Frete",
    seriePlaceholder: "Ex: Frete 1 - Fornecedor X",
    shots: [
      { id: "material_caminhao", nome: "MATERIAL NO CAMINHÃO", descricao: "Fotografe o material carregado no veículo antes de sair" },
      { id: "amarracao", nome: "AMARRAÇÃO / FIXAÇÃO", descricao: "Mostre como o material está preso e protegido para o transporte" },
      { id: "placa", nome: "PLACA DO VEÍCULO", descricao: "Fotografe a placa do caminhão/veículo claramente" },
    ],
  },
  "Frete para Marmoraria": {
    serieLabel: "Frete",
    seriePlaceholder: "Ex: Frete 1, Frete 2",
    shots: [
      { id: "material_caminhao", nome: "MATERIAL NO CAMINHÃO", descricao: "Fotografe o material carregado no veículo antes de sair" },
      { id: "amarracao", nome: "AMARRAÇÃO / FIXAÇÃO", descricao: "Mostre como o material está preso e protegido para o transporte" },
      { id: "placa", nome: "PLACA DO VEÍCULO", descricao: "Fotografe a placa do caminhão/veículo claramente" },
    ],
  },
  "Medição in Loco": {
    serieLabel: "Ambiente",
    seriePlaceholder: "Ex: Banheiro Master, Cozinha",
    shots: [
      { id: "visao_geral", nome: "VISÃO GERAL DO AMBIENTE", descricao: "Fotografe o ambiente inteiro, mostrando onde o material será instalado" },
      { id: "detalhe_medicao", nome: "DETALHE DA MEDIÇÃO / GABARITO", descricao: "Aproxime para mostrar gabarito, trena ou referências de medida" },
      { id: "referencias", nome: "REFERÊNCIAS / DETALHES CONSTRUTIVOS", descricao: "Pontos críticos: rasgos, encaixes, esquadrias, instalações" },
    ],
  },
  "Início da Produção": {
    serieLabel: "Peça",
    seriePlaceholder: "Ex: Bancada Banheiro Master, Soleira Entrada",
    shots: [
      { id: "antes_corte", nome: "ANTES DO CORTE", descricao: "Fotografe a chapa marcada com os contornos da peça a ser cortada" },
      { id: "durante", nome: "DURANTE A PRODUÇÃO", descricao: "Mostre o corte, polimento ou beneficiamento em andamento" },
      { id: "finalizada", nome: "PEÇA FINALIZADA", descricao: "Fotografe a peça pronta, antes de embalar/enviar" },
    ],
  },
  "Frete para Obra": {
    serieLabel: "Frete",
    seriePlaceholder: "Ex: Frete 1, Frete 2",
    shots: [
      { id: "material_caminhao", nome: "MATERIAL NO CAMINHÃO", descricao: "Fotografe o material carregado no veículo antes de sair" },
      { id: "amarracao", nome: "AMARRAÇÃO / FIXAÇÃO", descricao: "Mostre como o material está preso e protegido para o transporte" },
      { id: "placa", nome: "PLACA DO VEÍCULO", descricao: "Fotografe a placa do caminhão/veículo claramente" },
    ],
  },
  "Início da Colocação": {
    serieLabel: "Ambiente",
    seriePlaceholder: "Ex: Banheiro Master, Cozinha",
    shots: [
      { id: "antes", nome: "LOCAL ANTES DA COLOCAÇÃO", descricao: "Fotografe o local ANTES de iniciar a instalação (estado inicial)" },
      { id: "inicio", nome: "INÍCIO DO TRABALHO", descricao: "Material posicionado no local, equipe iniciando a instalação" },
    ],
  },
  "Conclusão": {
    serieLabel: "Ambiente",
    seriePlaceholder: "Ex: Banheiro Master, Cozinha",
    shots: [
      { id: "vista_geral", nome: "VISTA GERAL", descricao: "Fotografe todo o ambiente concluído, de frente, mostrando o resultado completo" },
      { id: "detalhe_esquerdo", nome: "DETALHE LADO ESQUERDO", descricao: "Fotografe o detalhe do lado ESQUERDO da instalação" },
      { id: "detalhe_direito", nome: "DETALHE LADO DIREITO", descricao: "Fotografe o detalhe do lado DIREITO da instalação" },
    ],
  },
  "Pós-obra": {
    serieLabel: "Momento",
    seriePlaceholder: "Ex: Cliente no banheiro, Detalhe premium da cozinha",
    shots: [
      { id: "cliente_no_ambiente", nome: "AMBIENTE COM CLIENTE", descricao: "Cliente no ambiente já entregue (uso real)" },
      { id: "detalhe_premium", nome: "DETALHE PREMIUM / ÂNGULO ESPECIAL", descricao: "Ângulo especial valorizando o resultado (capa de portfolio)" },
    ],
  },
};

// Etapas SEM wizard de fotos (apenas observação/upload de docs/toggle)
export const ETAPAS_SEM_WIZARD = ["Aprovação do Orçamento", "Aprovação do Projeto"];

// Config especial para etapas de RETRABALHO — identificadas por etapa.kind === "retrabalho"
// (não vai pelo nome porque retrabalhos podem ter qualquer nome customizado).
export const RETRABALHO_CONFIG = {
  serieLabel: "Item",
  seriePlaceholder: "Ex: Bancada Banheiro Master, Soleira Quebrada",
  shots: [
    { id: "problema", nome: "PROBLEMA IDENTIFICADO", descricao: "Fotografe o defeito/quebra/erro que motivou o retrabalho" },
    { id: "durante", nome: "DURANTE O RETRABALHO", descricao: "Mostre a equipe executando o conserto/refazimento" },
    { id: "resultado", nome: "RESULTADO FINAL", descricao: "Fotografe a peça/ambiente após o retrabalho concluído" },
  ],
};

export function getEtapaConfig(etapaNome) { return WIZARD_ETAPA_CONFIG[etapaNome] || null; }
export function getFotosObrigatorias(etapaNome) {
  return WIZARD_ETAPA_CONFIG[etapaNome]?.shots || [];
}
// Helper unificado: aceita o objeto etapa completo e devolve config (retrabalho OU normal)
export function resolveEtapaConfig(etapaObj) {
  if (!etapaObj) return null;
  if (etapaObj.kind === "retrabalho") return RETRABALHO_CONFIG;
  return WIZARD_ETAPA_CONFIG[etapaObj.nome] || null;
}

// ======================================================================
// FotoWizardEtapa — Wizard de séries repetíveis
// Props:
//   obra            — objeto da obra (precisa de id + fotos atuais p/ derivar séries)
//   etapaNome       — string (nome exato da etapa)
//   isAdmin         — bool (permite deletar/upload PC)
//   onClose()       — callback ao fechar
//   onReload()      — callback para recarregar a obra (apos save)
// ======================================================================
export default function FotoWizardEtapa({ obra, etapaNome, isAdmin, onClose, onReload }) {
  // Localiza objeto etapa (precisa pra detectar kind === "retrabalho")
  const etapaObj = (obra?.etapas || []).find(e => e.nome === etapaNome);
  const cfg = resolveEtapaConfig(etapaObj || { nome: etapaNome });
  const obraId = obra?.id;
  const token = localStorage.getItem("cps_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Deriva séries existentes a partir das fotos da obra que pertencem a esta etapa.
  // Cada série = grupo de fotos com mesmo serie_nome.
  const existingSeries = React.useMemo(() => {
    const out = {};
    (obra?.fotos || []).forEach(f => {
      if (f.etapa !== etapaNome) return;
      const sname = f.serie_nome || "_sem_serie";
      if (!out[sname]) out[sname] = { nome: sname === "_sem_serie" ? "" : sname, fotos: [] };
      out[sname].fotos.push(f);
    });
    return Object.values(out);
  }, [obra?.fotos, etapaNome]);

  // Modo: "menu" (lista séries existentes + nova) | "newSeries" (form criar série) | "shooting" (tirando fotos)
  const [mode, setMode] = useState("menu");
  const [activeSerie, setActiveSerie] = useState(null);  // { nome, extras{}, observacao, fotos:{shotId: {url, preview, status}} }
  const [shotIdx, setShotIdx] = useState(0);
  const [pendingFile, setPendingFile] = useState(null);  // File + preview URL aguardando confirmar/refazer
  const [pendingPreview, setPendingPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const fileRef = useRef(null);
  const adminFileRef = useRef(null);

  if (!cfg) {
    return (
      <div data-testid="foto-wizard-empty" style={styles.overlay}>
        <button onClick={onClose} style={styles.closeBtn} data-testid="foto-wizard-close">×</button>
        <div style={styles.emptyBox}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>📷</div>
          <div style={{ fontSize: 18, color: "#cdc8be" }}>
            Esta etapa não possui wizard configurado.
          </div>
        </div>
      </div>
    );
  }

  const startNewSerie = () => {
    setActiveSerie({
      nome: "",
      extras: Object.fromEntries((cfg.extras || []).map(e => [e.key, e.default || ""])),
      observacao: "",
      shotsDone: 0,
    });
    setShotIdx(0);
    setMode("newSeries");
  };

  const confirmSerieName = () => {
    if (!activeSerie?.nome?.trim()) {
      setErrorMsg(`Dê um nome para ${cfg.serieLabel.toLowerCase()} antes de continuar.`);
      return;
    }
    setErrorMsg("");
    setMode("shooting");
  };

  const onFilePicked = (file) => {
    if (!file) return;
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
    setErrorMsg("");
  };

  const confirmarFoto = async () => {
    if (!pendingFile || !activeSerie) return;
    setUploading(true);
    setErrorMsg("");
    try {
      const shot = cfg.shots[shotIdx];
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("etapa", etapaNome);
      fd.append("legenda", shot.nome);
      fd.append("categoria", "obra");
      fd.append("serie_nome", activeSerie.nome.trim());
      if (activeSerie.extras?.serie_origem) fd.append("serie_origem", activeSerie.extras.serie_origem);
      if (activeSerie.observacao) fd.append("serie_observacao", activeSerie.observacao);
      fd.append("foto_indice", String(shotIdx + 1));
      await axios.post(`${API}/obras/${obraId}/upload`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setPendingFile(null);
      setPendingPreview("");
      const nextIdx = shotIdx + 1;
      if (nextIdx >= cfg.shots.length) {
        // Série completa — recarrega e volta ao menu
        await onReload?.();
        setActiveSerie(null);
        setShotIdx(0);
        setMode("menu");
      } else {
        setShotIdx(nextIdx);
        await onReload?.();
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e.message || "Erro ao enviar foto");
    } finally {
      setUploading(false);
    }
  };

  const refazerFoto = () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(null);
    setPendingPreview("");
  };

  const onAdminUpload = async (files) => {
    if (!files || files.length === 0 || !isAdmin) return;
    setUploading(true);
    setErrorMsg("");
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("etapa", etapaNome);
        fd.append("legenda", `${etapaNome} — Admin upload`);
        fd.append("categoria", "obra");
        await axios.post(`${API}/obras/${obraId}/upload`, fd, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
      }
      await onReload?.();
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  const deletarFoto = async (foto) => {
    if (!isAdmin) return;
    if (!window.confirm(`Deletar esta foto?\n${foto.legenda || foto.filename || ""}`)) return;
    try {
      await axios.delete(`${API}/obras/${obraId}/foto/${foto.id}`, { headers });
      await onReload?.();
    } catch (e) {
      alert("Erro ao deletar: " + (e?.response?.data?.detail || e.message));
    }
  };

  // ---------- RENDER ----------
  return (
    <div data-testid="foto-wizard" style={styles.overlay}>
      <button onClick={onClose} style={styles.closeBtn} data-testid="foto-wizard-close" aria-label="Fechar">×</button>

      <div style={styles.header}>
        <div style={styles.etapaLabel}>{etapaNome}</div>
        <div style={styles.subLabel}>
          {cfg.shots.length} foto{cfg.shots.length === 1 ? "" : "s"} por {cfg.serieLabel.toLowerCase()}
          {" · "}
          {existingSeries.filter(s => s.nome).length} {cfg.serieLabel.toLowerCase()}(s) cadastrado(s)
        </div>
      </div>

      {/* ===== MODO MENU (lista séries existentes + nova) ===== */}
      {mode === "menu" && (
        <div style={styles.content}>
          <div style={styles.serieList} data-testid="wizard-serie-list">
            {existingSeries.filter(s => s.nome).map(serie => (
              <div key={serie.nome} style={styles.serieCard} data-testid={`wizard-serie-${serie.nome}`}>
                <div style={styles.serieCardHeader}>
                  <div style={styles.serieCardName}>{serie.nome}</div>
                  <div style={styles.serieCardCount}>
                    {serie.fotos.length} / {cfg.shots.length} fotos
                  </div>
                </div>
                <div style={styles.thumbsRow}>
                  {cfg.shots.map((shot, i) => {
                    const f = serie.fotos.find(x => x.foto_indice === i + 1) ||
                              serie.fotos.find(x => x.legenda === shot.nome);
                    return (
                      <div key={shot.id} style={styles.thumb} title={shot.nome}>
                        {f ? (
                          <>
                            <img src={f.url} alt={shot.nome}
                                 style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                            {f.uploaded_by_role === "admin" && (
                              <div style={styles.adminBadge} title="Enviada pelo admin">📸</div>
                            )}
                            {isAdmin && (
                              <button
                                onClick={() => deletarFoto(f)}
                                style={styles.deleteBtn}
                                title="Deletar foto"
                                data-testid={`wizard-del-${f.id}`}
                              >🗑</button>
                            )}
                          </>
                        ) : (
                          <div style={styles.thumbMissing}>{i + 1}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <button onClick={startNewSerie} style={styles.bigBtnPrimary} data-testid="wizard-add-serie">
            + ADICIONAR {cfg.serieLabel.toUpperCase()}
          </button>

          {isAdmin && (
            <>
              <input
                ref={adminFileRef}
                type="file"
                accept="image/*,video/mp4,video/quicktime,.mov"
                multiple
                style={{ display: "none" }}
                onChange={e => { onAdminUpload(e.target.files); e.target.value = ""; }}
                data-testid="wizard-admin-input"
              />
              <button
                onClick={() => adminFileRef.current?.click()}
                disabled={uploading}
                style={styles.bigBtnSecondaryWide}
                data-testid="wizard-admin-upload"
              >
                {uploading ? "Enviando…" : "📁 ADMIN · Upload do computador"}
              </button>
            </>
          )}

          <button onClick={onClose} style={styles.bigBtnGhost} data-testid="wizard-close-menu">
            Fechar
          </button>
        </div>
      )}

      {/* ===== MODO NEW SERIES (criar nome + extras) ===== */}
      {mode === "newSeries" && activeSerie && (
        <div style={styles.content} data-testid="wizard-new-serie">
          <div style={styles.stepLabel}>NOVA SÉRIE — {cfg.serieLabel.toUpperCase()}</div>
          <h1 style={styles.fotoNome}>Identifique esta série</h1>
          <p style={styles.fotoDescricao}>
            Dê um nome curto para o(a) {cfg.serieLabel.toLowerCase()} — assim fica fácil acompanhar depois.
          </p>

          <input
            value={activeSerie.nome}
            onChange={e => setActiveSerie(s => ({ ...s, nome: e.target.value }))}
            placeholder={cfg.seriePlaceholder}
            data-testid="wizard-serie-name"
            style={styles.inputBig}
            autoFocus
          />

          {(cfg.extras || []).map(ex => (
            <div key={ex.key} style={{ width: "100%", maxWidth: 460, marginTop: 18 }}>
              <label style={styles.fieldLabel}>{ex.label}</label>
              {ex.type === "select" ? (
                <select
                  value={activeSerie.extras[ex.key] || ""}
                  onChange={e => setActiveSerie(s => ({ ...s, extras: { ...s.extras, [ex.key]: e.target.value } }))}
                  data-testid={`wizard-extra-${ex.key}`}
                  style={styles.inputBig}
                >
                  {ex.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : null}
            </div>
          ))}

          <div style={{ width: "100%", maxWidth: 460, marginTop: 18 }}>
            <label style={styles.fieldLabel}>Observação (opcional)</label>
            <textarea
              value={activeSerie.observacao}
              onChange={e => setActiveSerie(s => ({ ...s, observacao: e.target.value }))}
              placeholder="Detalhes importantes desta série…"
              data-testid="wizard-serie-obs"
              style={{ ...styles.inputBig, minHeight: 80, resize: "vertical" }}
              maxLength={300}
            />
          </div>

          {errorMsg && <div style={styles.errorBox}>⚠ {errorMsg}</div>}

          <button onClick={confirmSerieName} style={{ ...styles.bigBtnPrimary, marginTop: 24 }} data-testid="wizard-confirm-name">
            COMEÇAR A TIRAR FOTOS →
          </button>
          <button onClick={() => { setActiveSerie(null); setMode("menu"); }} style={styles.bigBtnGhost}>
            Voltar
          </button>
        </div>
      )}

      {/* ===== MODO SHOOTING (tira foto -> preview -> confirma/refaz) ===== */}
      {mode === "shooting" && activeSerie && (
        <div style={styles.content} data-testid={`wizard-shooting-${shotIdx}`}>
          <div style={styles.serieBadge}>📌 {activeSerie.nome}</div>

          {/* Dots */}
          <div style={styles.dots}>
            {cfg.shots.map((_, i) => {
              const done = i < shotIdx;
              const current = i === shotIdx;
              return (
                <div key={i} style={{
                  ...styles.dot,
                  ...(done ? styles.dotDone : {}),
                  ...(current ? styles.dotCurrent : {}),
                }} />
              );
            })}
          </div>

          <div style={styles.stepLabel}>FOTO {shotIdx + 1} DE {cfg.shots.length}</div>
          <h1 style={styles.fotoNome} data-testid="foto-wizard-nome">{cfg.shots[shotIdx].nome}</h1>
          <p style={styles.fotoDescricao} data-testid="foto-wizard-descricao">{cfg.shots[shotIdx].descricao}</p>

          {errorMsg && <div style={styles.errorBox}>⚠ {errorMsg}</div>}

          {/* Preview (após escolha do arquivo, antes do upload) */}
          {pendingPreview ? (
            <>
              <div style={styles.previewWrap} data-testid="wizard-preview">
                <img src={pendingPreview} alt="preview"
                     style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
              <div style={styles.actionsRow}>
                <button onClick={refazerFoto} disabled={uploading} style={styles.bigBtnGhost} data-testid="wizard-refazer">
                  ↻ Refazer
                </button>
                <button onClick={confirmarFoto} disabled={uploading} style={styles.bigBtnPrimary} data-testid="wizard-confirmar">
                  {uploading ? "Enviando…" : "✓ Confirmar"}
                </button>
              </div>
            </>
          ) : (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={e => { onFilePicked(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="foto-wizard-input"
              />
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                      style={styles.bigBtnCamera} data-testid="foto-wizard-tirar">
                📷 TIRAR FOTO
              </button>
            </>
          )}

          <button onClick={() => { setActiveSerie(null); setShotIdx(0); setMode("menu"); }}
                  style={{ ...styles.bigBtnGhost, marginTop: 14 }}>
            Pausar e voltar
          </button>
        </div>
      )}
    </div>
  );
}

// ============================== STYLES ==============================
const styles = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "linear-gradient(180deg, #0a0a0c 0%, #15141a 100%)",
    color: "#f5f0e8",
    fontFamily: "'Inter', system-ui, sans-serif",
    overflowY: "auto",
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "20px 16px 60px",
  },
  closeBtn: {
    position: "fixed", top: 18, right: 22, zIndex: 10000,
    background: "rgba(184,147,90,0.15)", color: "#f5f0e8",
    border: "1px solid rgba(184,147,90,0.4)",
    width: 48, height: 48,
    fontSize: 28, lineHeight: 1, cursor: "pointer",
    borderRadius: "50%",
  },
  header: { width: "100%", maxWidth: 720, textAlign: "center", marginBottom: 28, marginTop: 8 },
  etapaLabel: { fontSize: 11, letterSpacing: ".4em", color: "#b8935a", textTransform: "uppercase", marginBottom: 8, fontWeight: 600 },
  subLabel: { fontSize: 12, color: "#8a857a", letterSpacing: ".05em" },
  content: { width: "100%", maxWidth: 600, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" },
  serieBadge: {
    background: "rgba(184,147,90,0.15)", border: "1px solid rgba(184,147,90,0.4)",
    padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#d4b27a",
    letterSpacing: ".1em", marginBottom: 16, borderRadius: 999,
  },
  dots: { display: "flex", gap: 10, justifyContent: "center", marginBottom: 18 },
  dot: { width: 14, height: 14, borderRadius: "50%", background: "transparent", border: "2px solid rgba(184,147,90,0.4)", transition: "all 0.3s" },
  dotCurrent: { border: "2px solid #b8935a", boxShadow: "0 0 0 4px rgba(184,147,90,0.2)", transform: "scale(1.2)" },
  dotDone: { background: "#5fa86c", border: "2px solid #5fa86c" },
  stepLabel: { fontSize: 13, letterSpacing: ".3em", color: "#8a857a", textTransform: "uppercase", marginBottom: 14, fontWeight: 600 },
  fotoNome: {
    fontSize: "clamp(28px, 6vw, 48px)", fontWeight: 700, letterSpacing: "-0.02em",
    color: "#f5f0e8", margin: "0 0 14px 0", lineHeight: 1.1, fontFamily: "'Fraunces', serif",
  },
  fotoDescricao: { fontSize: 16, color: "#cdc8be", lineHeight: 1.5, marginBottom: 28, maxWidth: 480 },
  fieldLabel: {
    display: "block", fontSize: 10, letterSpacing: ".2em", color: "#8a857a",
    textTransform: "uppercase", fontWeight: 600, marginBottom: 8, textAlign: "left",
  },
  inputBig: {
    width: "100%", maxWidth: 460, padding: "14px 18px", fontSize: 16,
    background: "rgba(255,255,255,0.04)", color: "#f5f0e8",
    border: "1px solid rgba(184,147,90,0.3)", borderRadius: 4,
    outline: "none", fontFamily: "inherit",
  },
  previewWrap: {
    width: "100%", maxWidth: 460, aspectRatio: "1/1", background: "#0a0a0c",
    border: "2px solid #b8935a", marginBottom: 18, overflow: "hidden", borderRadius: 4,
  },
  actionsRow: { display: "flex", gap: 12, width: "100%", maxWidth: 460, marginTop: 4 },
  bigBtnCamera: {
    width: "100%", maxWidth: 460, minHeight: 100,
    background: "linear-gradient(135deg, #b8935a 0%, #d4b27a 100%)", color: "#0d0d0f",
    border: "none", padding: "24px 20px", fontSize: 22, fontWeight: 700,
    letterSpacing: "0.08em", cursor: "pointer", borderRadius: 6,
    boxShadow: "0 8px 30px rgba(184,147,90,0.45)", marginBottom: 14,
  },
  bigBtnPrimary: {
    width: "100%", maxWidth: 460, minHeight: 64,
    background: "linear-gradient(135deg, #b8935a 0%, #d4b27a 100%)", color: "#0d0d0f",
    border: "none", padding: "16px", fontSize: 15, fontWeight: 700,
    letterSpacing: "0.08em", cursor: "pointer", borderRadius: 6,
    boxShadow: "0 6px 22px rgba(184,147,90,0.35)", marginTop: 18,
    textTransform: "uppercase", flex: 1,
  },
  bigBtnSecondaryWide: {
    width: "100%", maxWidth: 460, minHeight: 56,
    background: "transparent", color: "#b8935a",
    border: "1px solid rgba(184,147,90,0.4)",
    padding: "14px", fontSize: 14, fontWeight: 600, cursor: "pointer",
    borderRadius: 4, marginTop: 12, letterSpacing: ".06em",
  },
  bigBtnGhost: {
    background: "transparent", color: "#cdc8be",
    border: "1px solid rgba(255,255,255,0.15)",
    padding: "12px 24px", fontSize: 13, cursor: "pointer",
    borderRadius: 4, letterSpacing: "0.05em", marginTop: 16, flex: 1,
  },
  errorBox: {
    background: "rgba(196,122,122,0.15)", border: "1px solid #c47a7a", color: "#f5a5a5",
    padding: "10px 18px", marginBottom: 14, fontSize: 14, borderRadius: 4, maxWidth: 460,
  },
  serieList: { display: "flex", flexDirection: "column", gap: 14, width: "100%", marginBottom: 12 },
  serieCard: {
    background: "rgba(255,255,255,0.025)", border: "1px solid #2b2833",
    padding: "14px 16px", borderRadius: 4, textAlign: "left",
  },
  serieCardHeader: { display: "flex", justifyContent: "space-between", marginBottom: 10, alignItems: "center" },
  serieCardName: { fontSize: 16, fontWeight: 600, color: "#f5f0e8" },
  serieCardCount: { fontSize: 11, letterSpacing: ".15em", color: "#8a857a", textTransform: "uppercase" },
  thumbsRow: { display: "flex", gap: 6 },
  thumb: {
    width: 60, height: 60, background: "#0a0a0c", border: "1px solid #2b2833",
    overflow: "hidden", position: "relative", borderRadius: 3, flexShrink: 0,
  },
  thumbMissing: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "100%", height: "100%", color: "#3a3a40", fontSize: 18, fontWeight: 700,
  },
  adminBadge: {
    position: "absolute", top: 2, right: 2, background: "#b8935a", color: "#0d0d0f",
    fontSize: 10, width: 18, height: 18, display: "flex", alignItems: "center",
    justifyContent: "center", borderRadius: "50%", fontWeight: 700,
  },
  deleteBtn: {
    position: "absolute", bottom: 2, right: 2, background: "rgba(196,122,122,0.95)",
    color: "#fff", border: "none", width: 22, height: 22, borderRadius: "50%",
    cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: 1, padding: 0,
  },
  emptyBox: { maxWidth: 380, margin: "auto", padding: 30, textAlign: "center" },
};
